import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import type { PrimaryDiagnosisFitness } from '../services/api/types.gen';
import { fitnessPresentation } from '../utils/primaryDiagnosisFitness';

interface PrimaryFitnessConfirmDialogProps {
  /** The code being seated as primary; null closes the dialog. */
  code: string | null;
  fitness: PrimaryDiagnosisFitness | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Discouragement, not refusal.
 *
 * Some payers accept codes PDGM will not group, so this never blocks — it makes
 * the coder look once and say yes. The confirm action stays plainly labelled and
 * un-destructive: choosing this code is a legitimate act, just an unusual one.
 * Cancel is the default focus so a coder who reached here by accident (a drag
 * that landed one row too high) leaves by pressing Enter.
 */
const PrimaryFitnessConfirmDialog: React.FC<
  PrimaryFitnessConfirmDialogProps
> = ({ code, fitness, onConfirm, onCancel }) => {
  const presentation = fitnessPresentation(fitness);

  return (
    <Dialog open={code !== null} onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Use {code} as the primary diagnosis?</DialogTitle>
          <DialogDescription>
            {presentation
              ? `${presentation.message} Some payers still accept it. Most will not.`
              : // Reached when the fitness lookup has not resolved or failed.
                // Say that, rather than asserting a verdict we do not have.
                'This code could not be checked against the CMS grouper. It may not group for Medicare.'}
          </DialogDescription>
        </DialogHeader>
        {/* The emphasis is deliberate and inverted from the usual dialog: the
            recommended path carries the weight, the override is quiet but
            plainly clickable. A filled "Use anyway" would put the visual pull
            on the action this feature exists to discourage. Not destructive
            styling either — choosing the code is legitimate for some payers,
            just unusual. */}
        <DialogFooter>
          <Button variant='ghost' onClick={onConfirm}>
            Use anyway
          </Button>
          <Button onClick={onCancel} autoFocus>
            Choose another code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrimaryFitnessConfirmDialog;
